import { useEffect, useState } from 'react';
import axios from 'axios';
import useConsole from '../Console/useConsole';
import utils from '../../utils/utils';
import useDatasetGrid from '../useDatasetGrid';
import ApiEndpoints from '../../const/ApiEndpoints';
import useProject from '../Project/useProject';
import MODULE_SUB_MODULE_OPERATIONS_TREE from '../../const/ModuleSubModuleOperationsTree';
import useDataIngestion from '../DM/useDataIngestion';
import useModelMetaData from '../useModelMetaData';
import { DATASET_REQUIRED_FIELDS } from '../../const/CommonConst';

const MT_BASE_URL = ApiEndpoints.MODULE_BASE_URL.MT;

/**
 * @function useDriftAnalysis
 * @description hook for feature interpretation module of model interpretation module
 * @param {function} fetchModuleTasks - function for fetching module tasks
 * @param {array} datasetList - list of datasets
 * @param {function} fetchDatasetList - function for fetching dataset list
 * @param {function} fetchDatasetRowsWithPagination - function for fetching dataset rows with pagination
 * @param {array} datasetRowsWithPagination - list of dataset rows with pagination
 * @param {function} setDatasetRowsWithPagination - function for setting dataset rows with pagination
 * @param {number} totalDatasetRows - total number of dataset rows
 * @param {number} datasetRowsPageCount - dataset rows page count
 * @param {boolean} isLoading - boolean for showing loader
 * @param {function} updateDatasetCells - function for updating dataset cells
 * @param {object} project - project object
 * @returns {object} - returning multiple methods and states for classification stats module of model interpretation module
 * @example const {fetchModuleTasks,fetchDatasetList,datasetList}=useDriftAnalysis()
 */
export default function useDriftAnalysis() {
  const {
    fetchModuleTasks,
    datasetList,
    fetchDatasetList,
    fetchDatasetRowsWithPagination,
    datasetRowsWithPagination,
    setDatasetRowsWithPagination,
    totalDatasetRows,
    datasetRowsPageCount,
    isLoading,
    updateDatasetCells,
    project,
  } = useProject();

  const {
    renameColumn,
    deleteColumn,
    duplicateColumn,
    addColumn,
    deleteRow,
    duplicateRow,
    addRow,
    tasks: gridTasks,
    taskCount: gridTaskCount,
    fetchTasks: fetchGridTasks,
    terminateTask: terminateGridTask,
    manageSpaces,
    manageCases,
    splitColumn,
    findAndReplace,
    sortColumn,
    analyse,
    embeddedValues,
    standardisationError,
    integrityRules,
    uniqueness,
    scores,
    fetchGridTaskData,
    findDuplicateRows,
    findDuplicateColumns,
    findAndDeleteDuplicateRows,
    findAndDeleteDuplicateColumns,
    setGridTaskResultsData,
    gridTaskResultsData,
    resetGridTaskResultsData,
    markTaskReadUnreadGrid,
    valueError,
    findBlankValuePercentage,
    replaceBlankValues,
    findRegexPercentage,
    gridOperationRerun,
  } = useDatasetGrid();

  const {
    consoleModuleTasks,
    consoleAsyncTaskResults,
    fetchAsyncResultConsole,
    terminateTaskConsole,
    markTaskReadUnreadConsole,
    fetchModuleTasksConsole,
    setConsoleModuleTasks,
    fetchUdfs,
    deleteUdf,
    createUdf,
    createJob,
    updateUdf,
    udfs,
    selectedUdf,
    setSelectedUdf,
    runUdf,
    githubIntegration,
    fetchUdfDetails,
    fetchDatasetColumnDetailsForConsole,
    setConsoleAsyncTaskResults,
    fetchAllModels,
    fetchTableData,
    onDownloadCSV,
    fetchConsoleLibraries,
    consoleRerunOperation,
    fetchConsoleFileList,
  } = useConsole();
  const { markDatasetAsReadyToUse } = useDataIngestion();
  const { addResultToModelMetaData, fetchModelMetaDataSchema } =
    useModelMetaData();

  // For Active Dataset
  const [activeDataset, setActiveDataset] = useState('');

  const totalPageCount = 'x-total-count';

  useEffect(() => {
    if (activeDataset?.id) {
      datasetList.forEach((data) => {
        if (data.id === activeDataset.id) {
          setActiveDataset(data);
        }
      });
    }
  }, [datasetList]);
  // For Active Operation
  const [activeOperation, setActiveOperation] = useState(
    MODULE_SUB_MODULE_OPERATIONS_TREE.MT.subModules.DA.operations.DD
  );

  // Feature Interpretation Tasks
  const [moduleTasks, setModuleTasks] = useState([]);

  // For Response Data : Graphs or Tables or Table ID
  const [taskDetails, updateTaskDetails] = useState({});

  // For Column Details of Active Dataset
  const [datasetColumnDetails, setDatasetColumnDetails] = useState([]);

  const [totalModuleTask, setTotalModuleTask] = useState(0);
  const [totalModuleTaskConsole, setTotalModuleTaskConsole] = useState(0);
  const [taskCount, setTaskCount] = useState({});

  // reset data on Operation and Dataset Change
  const resetTaskDetails = () => {
    updateTaskDetails({});
  };

  const submitDataDrift = (body) =>
    axios
      .post(`${MT_BASE_URL}${ApiEndpoints.MT.MT_DATA_DRIFT_SUBMIT}`, body)
      .then(
        (res) => res.data,
        (err) => {
          throw err;
        }
      );

  /**
   * @function fetchDAModuleTasks
   * @description API call for fetching sampling module tasks
   * @param {number} project_id - project id
   * @param {boolean} hideLoaderOnRequest - param for hiding loader in UI while calling an api.
   * @returns {undefined}
   * @example fetchDAModuleTasks(project_id,hideLoaderOnRequest);
   */
  // For Fetching the Tasks List

  const fetchDAModuleTasks = (
    projectId,
    hideLoaderOnRequest,
    pageIndex,
    pageSize,
    taskStatus
  ) => {
    if (projectId) {
      return fetchModuleTasks(
        MODULE_SUB_MODULE_OPERATIONS_TREE.MT.objKey,
        MODULE_SUB_MODULE_OPERATIONS_TREE.MT.subModules.DA.key,
        'GET_SS_TASKS',
        () => {},
        projectId,
        hideLoaderOnRequest,
        '',
        pageIndex,
        pageSize,
        taskStatus
      ).then((res) => {
        setTotalModuleTask(res.headers[totalPageCount]);
        if (pageIndex > 1) {
          setModuleTasks((prevModuleTasks) => [
            ...prevModuleTasks,
            ...(res?.data?.tasks || []),
          ]);
        } else {
          setModuleTasks([...res.data.tasks]);
        }
        return res;
      });
    }
  };

  /**
   * Fetches the count of module tasks.
   *
   * @param {string} projectId - The ID of the project.
   * @param {boolean} hideLoaderOnRequest - Whether to hide the loader on request.
   * @param {number} count - The count of tasks to fetch.
   * @return {Promise} A promise that resolves with the count of tasks.
   */
  const fetchModuleTasksCount = (projectId, hideLoaderOnRequest, count) => {
    if (projectId) {
      fetchModuleTasks(
        MODULE_SUB_MODULE_OPERATIONS_TREE.MT.objKey,
        MODULE_SUB_MODULE_OPERATIONS_TREE.MT.subModules.DA.key,
        'GET_SS_TASKS',
        () => {},
        projectId,
        hideLoaderOnRequest,
        '',
        null,
        null,
        null,
        count
      ).then((res) => {
        setTaskCount(res?.data);
      });
    }
  };

  /**
   * @function fetchDatasetColumnDetails
   * @description API call for fetching dataset column details
   * @param {string} datasetName - name of the dataset
   * @returns {Promise} AxiosPromise
   * @example fetchDatasetColumnDetails(datasetName);
   */
  // For fetching Active Dataset Columns
  const fetchDatasetColumnDetails = (datasetName) =>
    axios
      .post(
        `${
          ApiEndpoints.MODULE_BASE_URL.MT +
          ApiEndpoints.MT.DATASET_COLUMN_DETAILS
        }`,
        datasetName
      )
      .then(
        (res) => {
          setDatasetColumnDetails(res.data);
          return res;
        },
        (err) => {
          throw err;
        }
      );

  const fetchDatasetColumnDetailsWithModuleKey = (datasetName, mKey) =>
    axios
      .post(
        `${
          ApiEndpoints.MODULE_BASE_URL?.[mKey] +
          ApiEndpoints?.[mKey]?.DATASET_COLUMN_DETAILS
        }`,
        datasetName
      )
      .then(
        (res) => {
          return res;
        },
        (error) => {
          throw error;
        }
      );

  /**
   * @function fetchAsyncResult
   * @description API call for fetching asynchronous result
   * @param {string} taskID - task id
   * @param {object} additionTaskDetails - addition task details
   * @param {function} successCallback - Callback function to execute upon completion of API call
   * @returns {Promise} AxiosPromise
   * @example fetchAsyncResult(taskID, additionTaskDetails, successCallback);
   */
  // Fetch Asnyc Operation Result
  const fetchAsyncResult = (taskID, additionTaskDetails, successCallback) =>
    axios.get(`${MT_BASE_URL + ApiEndpoints.MT.GET_SS_TASKS}/${taskID}`).then(
      (res) => {
        if (res.data.task_status === 'SUCCESS') {
          Object.values(
            MODULE_SUB_MODULE_OPERATIONS_TREE.MT.subModules.DA.operations
          ).filter((data) => {
            if (
              data.key === additionTaskDetails.operation &&
              res.data?.parameters?.dataset_id_one
            ) {
              datasetList.forEach((dataset) => {
                if (dataset.id === Number(res.data.parameters.dataset_id_one)) {
                  setActiveDataset(dataset);
                }
              });

              updateTaskDetails(res.data);
              setActiveOperation(data);
              successCallback();
            }
            return null;
          });
        } else {
          updateTaskDetails({});
        }
      },
      () => {}
    );

  const fetchAsyncResultSplit = (taskID, type) =>
    axios
      .get(`${MT_BASE_URL + ApiEndpoints.MT.GET_SS_TASKS}/${taskID}/${type}/`)
      .then((res) => {
        return res;
      });

  /**
   * @function fetchAsyncResultConsoleAndActivateDataset
   * @description API call for fetching asynchronous result
   * @param {number}  taskID - task id
   * @param {object}  additionTaskDetails - addition task details
   * @returns  {Promise} AxiosPromise
   * @example  fetchAsyncResultConsoleAndActivateDataset(taskID, additionTaskDetails)
   */
  const fetchAsyncResultConsoleAndActivateDataset = (
    taskID,
    additionTaskDetails
  ) =>
    fetchAsyncResultConsole(taskID, additionTaskDetails).then(
      (res) => {
        if (res.data.task_status === 'SUCCESS') {
          if (utils.isNewDatasetCreatedByUdf(res.data)) {
            fetchDatasetList(
              project?.activeProject?.project_id,
              true,
              [DATASET_REQUIRED_FIELDS].join()
            );
          }
          if (res.data?.parameters?.table) {
            datasetList.forEach((dataset) => {
              if (dataset.name === res.data.parameters.table) {
                setActiveDataset({ ...dataset });
              }
            });
          }
        }
        return res;
      },
      (error) => {
        throw error;
      }
    );

  /**
   * @function terminateTask
   * @description API call for terminating task
   * @param {string} taskID - task id
 
   * @returns {Promise} AxiosPromise
   * @example terminateTask(taskID);
   */
  // For Terminating Task in Task Manager
  const terminateTask = (taskID) =>
    axios
      .delete(`${MT_BASE_URL + ApiEndpoints.MT.GET_SS_TASKS}/${taskID}`)
      .then((res) => res)
      .catch((e) => {
        throw e;
      });

  /**
   * @function mtRerunOperation
   * @description API call for reruning the operation
   * @param {object} body - body
   * @returns {Promise} AxiosPromise
   * @example mtRerunOperation(body);
   */
  const mtRerunOperation = (body) => {
    return axios
      .post(`${MT_BASE_URL + ApiEndpoints.MT.MT_RERUN}`, body)
      .then((res) => {
        return res;
      })
      .catch((e) => {
        throw e;
      });
  };

  /**
   * @function markTaskReadUnreadFeatureInterpretation
   * @description API call for mark task read unread of sampling sub module
   * @param {object} taskParams -task parameters of mark task read unread of sampling sub module
   * @param {function} successCallback - Callback function to execute upon completion of API call
   * @returns {Promise} AxiosPromise
   * @example markTaskReadUnreadFeatureInterpretation(taskParams,successCallback);
   */
  const markTaskReadUnreadFeatureInterpretation = (
    taskParams,
    successCallback
  ) =>
    axios
      .get(`${MT_BASE_URL + ApiEndpoints.MT.MT_JOB_QUEUE_MARK_READ_UNREAD}`, {
        params: taskParams,
      })
      .then(
        () => {
          successCallback();
        },
        () => {}
      );
  /**
   * @function fetchDAModuleTasksConsole
   * @description API call for fetching module tasks
   * @param {number} projectId - project id
   * @param {boolean} hideLoaderOnRequest - param for hiding loader in UI while calling an api.
   * @returns {undefined}
   * @example fetchDAModuleTasksConsole(projectId,hideLoaderOnRequest);
   */

  const fetchDAModuleTasksConsole = (
    projectId,
    hideLoaderOnRequest,
    pageIndex,
    pageSize,
    taskStatus
  ) => {
    if (projectId) {
      fetchModuleTasksConsole(
        MODULE_SUB_MODULE_OPERATIONS_TREE.MT.key,
        MODULE_SUB_MODULE_OPERATIONS_TREE.MT.subModules.DA.key,
        'GET_SS_TASKS',
        projectId,
        hideLoaderOnRequest,
        pageIndex,
        pageSize,
        taskStatus
      ).then((res) => {
        if (res.data) {
          setTotalModuleTaskConsole(res.headers[totalPageCount]);
          if (pageIndex > 1) {
            setConsoleModuleTasks([...consoleModuleTasks, ...res.data]);
          } else {
            setConsoleModuleTasks([...res.data]);
          }
        }
      });
    }
  };

  /**
   * @function addTableAndChartForMonitoring
   * @description API call for fetching table and chart column details
   * @param {object} payload - object containing project_id and data
   * @returns {Promise} AxiosPromise
   * @example addTableAndChartForMonitoring(datasetName);
   */
  const onAddTableChartClick = (payload) =>
    axios
      .post(
        `${
          ApiEndpoints.MODULE_BASE_URL.MT + ApiEndpoints.MT.ADD_TABLE_AND_CHART
        }`,
        payload
      )
      .then(
        (res) => res,
        (error) => {
          throw error;
        }
      );
  return {
    activeOperation,
    setActiveOperation,

    activeDataset,
    setActiveDataset,

    moduleTasks,
    fetchDAModuleTasks,
    setModuleTasks,
    totalModuleTask,
    taskCount,
    fetchModuleTasksCount,

    resetTaskDetails,
    taskDetails,

    fetchAsyncResult,
    datasetList,
    terminateTask,
    fetchDatasetList,

    submitDataDrift,

    updateDatasetCells,

    fetchDatasetRowsWithPagination,
    datasetRowsWithPagination,
    setDatasetRowsWithPagination,
    totalDatasetRows,
    datasetRowsPageCount,
    isLoading,
    onAddTableChartClick,

    renameColumn,
    duplicateColumn,
    deleteColumn,
    addColumn,
    deleteRow,
    duplicateRow,
    addRow,
    gridTasks,
    gridTaskCount,
    fetchGridTasks,
    terminateGridTask,
    manageSpaces,
    manageCases,
    splitColumn,
    findAndReplace,
    sortColumn,
    analyse,
    embeddedValues,
    standardisationError,
    integrityRules,
    uniqueness,
    scores,
    fetchGridTaskData,
    mtRerunOperation,
    setDatasetColumnDetails,
    datasetColumnDetails,
    fetchDatasetColumnDetails,
    fetchDatasetColumnDetailsWithModuleKey,
    markTaskReadUnreadFeatureInterpretation,
    project,
    findDuplicateRows,
    findDuplicateColumns,
    findAndDeleteDuplicateRows,
    findAndDeleteDuplicateColumns,
    setGridTaskResultsData,
    gridTaskResultsData,
    resetGridTaskResultsData,
    markTaskReadUnreadGrid,
    valueError,
    findBlankValuePercentage,
    replaceBlankValues,
    findRegexPercentage,
    gridOperationRerun,

    // console related
    consoleModuleTasks,
    totalModuleTaskConsole,
    consoleAsyncTaskResults,
    fetchAsyncResultConsoleAndActivateDataset,
    terminateTaskConsole,
    markTaskReadUnreadConsole,
    fetchDAModuleTasksConsole,
    fetchUdfs,
    deleteUdf,
    createUdf,
    createJob,
    updateUdf,
    udfs,
    selectedUdf,
    setSelectedUdf,
    runUdf,
    githubIntegration,
    fetchUdfDetails,
    fetchDatasetColumnDetailsForConsole,
    fetchAllModels,
    setConsoleAsyncTaskResults,
    fetchTableData,
    onDownloadCSV,
    fetchConsoleLibraries,
    consoleRerunOperation,
    markDatasetAsReadyToUse,
    addResultToModelMetaData,
    fetchModelMetaDataSchema,
    fetchConsoleFileList,
    fetchAsyncResultSplit,
  };
}
